import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { 
  Grid, 
  LinearProgress, 
  Typography, 
  Button, 
  Divider, 
  Card, 
  CardHeader, 
  CardContent, 
  Box, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Tooltip
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SendIcon from "@material-ui/icons/Send";
import PhoneIcon from "@material-ui/icons/Phone";
import MessageIcon from "@material-ui/icons/Message";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import ReplyIcon from "@material-ui/icons/Reply";
import BlockIcon from "@material-ui/icons/Block";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useDate } from "../../hooks/useDate";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { Doughnut } from 'react-chartjs-2';
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    marginBottom: theme.spacing(2),
  },
  summaryCards: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardIcon: {
    color: theme.palette.primary.main,
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  progressContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  statusChip: {
    color: '#fff',
    fontWeight: 'bold',
  },
  deliveredChip: {
    backgroundColor: theme.palette.success.main,
  },
  pendingChip: {
    backgroundColor: theme.palette.warning.main,
  },
  failedChip: {
    backgroundColor: theme.palette.error.main,
  },
  respondedChip: {
    backgroundColor: '#2196f3',
  },
  pieChartContainer: {
    height: 250,
    position: 'relative',
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 500,
    margin: theme.spacing(2, 0),
  },
  detailsCard: {
    marginBottom: theme.spacing(2),
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
}));

const CampaignMetaReport = () => {
  const classes = useStyles();
  const history = useHistory();
  const { campaignId } = useParams();

  const [campaign, setCampaign] = useState({});
  const [delivered, setDelivered] = useState(0);
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState(0);
  const [responded, setResponded] = useState(0);
  const [invalidWhatsapp, setInvalidWhatsapp] = useState(0);
  const [totalMessages, setTotalMessages] = useState(0);
  const [respondedMessages, setRespondedMessages] = useState(0);
  const [responseRate, setResponseRate] = useState(0);
  const [buttonClicks, setButtonClicks] = useState(0);
  const [textReplies, setTextReplies] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messageRows, setMessageRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();
  
  const [chartData, setChartData] = useState({
    labels: ['Entregues', 'Pendentes', 'Falhas'],
    datasets: [
      {
        data: [0, 0, 0],
        backgroundColor: [
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(255, 99, 132, 0.7)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
      },
    ],
  });
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página!");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    findCampaign();
  }, [campaignId]);

  const findCampaign = async () => {
    try {
      setLoading(true);
      
      const { data: campaignData } = await api.get(`/campaigns-meta/${campaignId}`);
      setCampaign(campaignData);
      
      const { data: stats } = await api.get(`/campaigns-meta/${campaignId}/stats`);
      const respondedCount = stats.respondedMessages || 0;
      const invalidCount = stats.invalidWhatsappCount || 0;
      setDelivered(stats.deliveredMessages || 0);
      setPending(stats.pendingMessages || 0);
      setFailed(stats.failedMessages || 0);
      setTotalMessages(stats.totalMessages || 0);
      setRespondedMessages(respondedCount);
      setResponded(respondedCount);
      setInvalidWhatsapp(invalidCount);
      setResponseRate(stats.responseRate || 0);
      setButtonClicks(stats.buttonClicks || 0);
      setTextReplies(stats.textReplies || 0);
      
      setChartData({
        labels: ['Entregues', 'Pendentes', 'Falhas'],
        datasets: [
          {
            data: [stats.deliveredMessages || 0, stats.pendingMessages || 0, stats.failedMessages || 0],
            backgroundColor: [
              'rgba(75, 192, 192, 0.7)',
              'rgba(255, 206, 86, 0.7)',
              'rgba(255, 99, 132, 0.7)',
            ],
            borderColor: [
              'rgba(75, 192, 192, 1)',
              'rgba(255, 206, 86, 1)',
              'rgba(255, 99, 132, 1)',
            ],
            borderWidth: 1,
          },
        ],
      });
      
      const { data: shippingResponse } = await api.get(`/campaigns-meta/${campaignId}/shipping?page=1&pageSize=${rowsPerPage}`);
      
      if (shippingResponse && shippingResponse.shipping) {
        const formattedRows = shippingResponse.shipping.map(item => ({
          id: item.id,
          number: item.number,
          templateName: item.templateName,
          deliveredAt: item.deliveredAt ? datetimeToClient(item.deliveredAt) : null,
          failedAt: item.failedAt ? datetimeToClient(item.failedAt) : null,
          respondedAt: item.respondedAt ? datetimeToClient(item.respondedAt) : null,
          responseType: item.responseType || null,
          errorMessage: item.errorMessage || '',
          createdAt: item.createdAt ? datetimeToClient(item.createdAt) : null,
          status: item.isInvalid ? 'invalid' : (item.respondedAt ? 'responded' : (item.deliveredAt ? 'delivered' : (item.failedAt ? 'failed' : 'pending')))
        }));
        
        setMessageRows(formattedRows);
      }
      
    } catch (error) {
      console.error("Erro ao buscar campanha:", error);
      toast.error("Erro ao carregar relatório da campanha");
    } finally {
      setLoading(false);
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return "Inativa";
      case "PROGRAMADA":
        return "Programada";
      case "EM_ANDAMENTO":
        return "Em Andamento";
      case "CANCELADA":
        return "Cancelada";
      case "FINALIZADA":
        return "Finalizada";
      default:
        return val;
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadMoreShipping(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    loadMoreShipping(1, parseInt(event.target.value, 10));
  };
  
  const loadMoreShipping = async (newPage = 1, pageSize = 10) => {
    try {
      setLoading(true);
      
      const { data: shippingResponse } = await api.get(`/campaigns-meta/${campaignId}/shipping?page=${newPage}&pageSize=${pageSize}`);
      
      if (shippingResponse && shippingResponse.shipping) {
        const formattedRows = shippingResponse.shipping.map(item => ({
          id: item.id,
          number: item.number,
          templateName: item.templateName,
          deliveredAt: item.deliveredAt ? datetimeToClient(item.deliveredAt) : null,
          failedAt: item.failedAt ? datetimeToClient(item.failedAt) : null,
          respondedAt: item.respondedAt ? datetimeToClient(item.respondedAt) : null,
          responseType: item.responseType || null,
          errorMessage: item.errorMessage || '',
          createdAt: item.createdAt ? datetimeToClient(item.createdAt) : null,
          status: item.isInvalid ? 'invalid' : (item.respondedAt ? 'responded' : (item.deliveredAt ? 'delivered' : (item.failedAt ? 'failed' : 'pending')))
        }));
        
        setMessageRows(formattedRows);
      }
    } catch (error) {
      console.error("Erro ao carregar dados de envio:", error);
      toast.error("Erro ao carregar dados de envio");
    } finally {
      setLoading(false);
    }
  };
  
  const formatPhoneNumber = (number) => {
    if (!number) return '';
    
    if (number.length > 15) {
      return `Grupo (${number.substring(0, 6)}...)`;
    }
    
    if (number.length === 12 && number.startsWith('55')) {
      const ddd = number.substring(2, 4);
      const firstPart = number.substring(4, 9);
      const lastPart = number.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    
    return number;
  };
  
  const getMessageStatusChip = (status) => {
    switch (status) {
      case 'invalid':
        return (
          <Chip 
            label="WhatsApp Inválido" 
            size="small" 
            icon={<BlockIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.failedChip}`} 
          />
        );
      case 'responded':
        return (
          <Chip 
            label="Respondido" 
            size="small" 
            icon={<ReplyIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.respondedChip}`} 
          />
        );
      case 'delivered':
        return (
          <Chip 
            label="Entregue" 
            size="small" 
            icon={<DoneAllIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.deliveredChip}`} 
          />
        );
      case 'pending':
        return (
          <Chip 
            label="Pendente" 
            size="small" 
            icon={<ScheduleIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.pendingChip}`} 
          />
        );
      case 'failed':
        return (
          <Chip 
            label="Falha" 
            size="small" 
            icon={<ErrorIcon fontSize="small" />} 
            className={`${classes.statusChip} ${classes.failedChip}`} 
          />
        );
      default:
        return <Chip label="Desconhecido" size="small" variant="outlined" />;
    }
  };

  const exportToExcel = async () => {
    try {
      toast.info("Gerando relatório Excel...");
      
      let allShippingData = [];
      let currentPage = 1;
      let hasMoreData = true;
      const pageSize = 1000;
      
      while (hasMoreData) {
        const { data: shippingResponse } = await api.get(`/campaigns-meta/${campaignId}/shipping?page=${currentPage}&pageSize=${pageSize}`);
        
        if (!shippingResponse || !shippingResponse.shipping) {
          break;
        }
        
        allShippingData = [...allShippingData, ...shippingResponse.shipping];
        hasMoreData = currentPage < shippingResponse.totalPages;
        currentPage++;
        
        toast.info(`Carregando dados... ${allShippingData.length} registros`);
      }
      
      if (allShippingData.length === 0) {
        toast.error("Nenhum dado encontrado");
        return;
      }

      const excelData = [
        ["Relatório de Campanha META", "", "", "", "", "", ""],
        [`Campanha: ${campaign.name}`, "", "", "", "", "", ""],
        [`Data: ${new Date().toLocaleString('pt-BR')}`, "", "", "", "", "", ""],
        ["", "", "", "", "", "", ""],
        ["ID", "Destinatário", "Template", "Criado em", "Entregue em", "Respondido em", "Status"],
      ];

      allShippingData.forEach(item => {
        const statusLabel = item.isInvalid ? 'WhatsApp Inválido' : (item.respondedAt ? 'Respondido' : (item.deliveredAt ? 'Entregue' : (item.failedAt ? 'Falha' : 'Pendente')));
        excelData.push([
          item.id || '',
          item.number || '',
          item.templateName || '',
          item.createdAt ? new Date(item.createdAt).toLocaleString('pt-BR') : '',
          item.deliveredAt ? new Date(item.deliveredAt).toLocaleString('pt-BR') : '',
          item.respondedAt ? new Date(item.respondedAt).toLocaleString('pt-BR') : '',
          statusLabel
        ]);
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      ws['!cols'] = [
        { wch: 10 },
        { wch: 20 },
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 }
      ];

      ws['!merges'] = [
        { s: { c: 0, r: 0 }, e: { c: 6, r: 0 } },
        { s: { c: 0, r: 1 }, e: { c: 6, r: 1 } },
        { s: { c: 0, r: 2 }, e: { c: 6, r: 2 } }
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Relatório Campanha META");

      const fileName = `campanha_meta_${campaign.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(wb, fileName);
      
      toast.success(`Relatório gerado! ${allShippingData.length} registros exportados.`);
      
    } catch (error) {
      console.error("Erro ao exportar:", error);
      toast.error("Erro ao gerar relatório Excel");
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} item style={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              style={{ marginRight: 10 }}
              onClick={() => history.push('/campaigns-meta')}
              startIcon={<ArrowBackIcon />}
            >
              Voltar
            </Button>
            <Title>Relatório da Campanha META: {campaign.name || "Carregando..."}</Title>
          </Grid>
        </Grid>
      </MainHeader>
      
      <Paper className={classes.mainPaper} variant="outlined">
        <Box mb={2}>
          <Typography variant="h5" component="h2" gutterBottom>
            {campaign.name || "Campanha"}
          </Typography>
          <Chip 
            label={formatStatus(campaign.status)} 
            color={campaign.status === "FINALIZADA" ? "primary" : "default"}
            style={{ fontWeight: 'bold', marginRight: 8 }}
          />
        </Box>
        
        <Grid container spacing={3} className={classes.summaryCards}>
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Status de Entrega
                </Typography>
                <Box className={classes.pieChartContainer}>
                  <Doughnut data={chartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <SendIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {totalMessages}
                </Typography>
                <Typography color="textSecondary">
                  Total de Mensagens
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <CheckCircleIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {delivered}
                </Typography>
                <Typography color="textSecondary">
                  Entregues
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <ErrorIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {failed}
                </Typography>
                <Typography color="textSecondary">
                  Falhas
                </Typography>
                {invalidWhatsapp > 0 && (
                  <Typography variant="caption" color="textSecondary" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    <BlockIcon style={{ fontSize: 14 }} />
                    {invalidWhatsapp} WhatsApp inválido{invalidWhatsapp > 1 ? 's' : ''}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* 📊 Cards de Engajamento */}
        <Typography variant="h6" className={classes.sectionTitle} style={{ marginTop: 32 }}>
          📊 Métricas de Engajamento
        </Typography>
        <Divider style={{ marginBottom: 16 }} />
        
        <Grid container spacing={3} className={classes.summaryCards}>
          <Grid item xs={12} md={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <DoneAllIcon className={classes.cardIcon} style={{ color: '#4caf50' }} />
                <Typography variant="h4" component="div">
                  {respondedMessages}
                </Typography>
                <Typography color="textSecondary">
                  Respostas Recebidas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Taxa de Resposta
                </Typography>
                <Typography variant="h3" component="div" style={{ color: '#2196f3', fontWeight: 'bold' }}>
                  {responseRate}%
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {respondedMessages} de {delivered} entregues
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <PhoneIcon className={classes.cardIcon} style={{ color: '#ff9800' }} />
                <Typography variant="h4" component="div">
                  {buttonClicks}
                </Typography>
                <Typography color="textSecondary">
                  Cliques em Botões
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <MessageIcon className={classes.cardIcon} style={{ color: '#9c27b0' }} />
                <Typography variant="h4" component="div">
                  {textReplies}
                </Typography>
                <Typography color="textSecondary">
                  Respostas em Texto
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Box className={classes.progressContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="body2" color="textSecondary">Progresso de Entrega</Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textPrimary">
                {delivered} de {totalMessages}
              </Typography>
            </Grid>
          </Grid>
          <LinearProgress
            variant="determinate"
            style={{ height: 10, borderRadius: 5, margin: "8px 0" }}
            value={(delivered / (totalMessages || 1)) * 100}
          />
        </Box>
        
        <Grid container spacing={3}>
          {campaign.whatsapp && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar}>
                      <WhatsAppIcon />
                    </Avatar>
                  }
                  title="Conexão WhatsApp Oficial"
                  subheader={campaign.whatsapp?.name || "Não especificado"}
                />
              </Card>
            </Grid>
          )}
          
          {campaign.contactList && (
            <Grid item xs={12} md={4}>
              <Card variant="outlined" className={classes.detailsCard}>
                <CardHeader
                  avatar={
                    <Avatar className={classes.avatar}>
                      <ListAltIcon />
                    </Avatar>
                  }
                  title="Lista de Contatos"
                  subheader={campaign.contactList?.name || "Não especificado"}
                />
              </Card>
            </Grid>
          )}
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <ScheduleIcon />
                  </Avatar>
                }
                title="Agendamento"
                subheader={datetimeToClient(campaign.scheduledAt) || "Não agendado"}
              />
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <EventAvailableIcon />
                  </Avatar>
                }
                title="Conclusão"
                subheader={datetimeToClient(campaign.completedAt) || "Não concluído"}
              />
            </Card>
          </Grid>
        </Grid>
        
        <Box mt={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <MessageIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Detalhes dos Envios
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={exportToExcel}
            >
              Exportar Excel
            </Button>
          </Box>
          <Divider />
          
          <TableContainer className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Destinatário</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell>Entregue em</TableCell>
                  <TableCell>Respondido em</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Erro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messageRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>
                      <Tooltip title={row.number}>
                        <span>{formatPhoneNumber(row.number)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.templateName}</TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>{row.deliveredAt || '-'}</TableCell>
                    <TableCell>{row.respondedAt || '-'}</TableCell>
                    <TableCell>{getMessageStatusChip(row.status)}</TableCell>
                    <TableCell>
                      {row.errorMessage && (
                        <Tooltip title={row.errorMessage}>
                          <ErrorIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {messageRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {loading ? "Carregando..." : "Nenhum envio encontrado"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalMessages}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default CampaignMetaReport;
